import i18n from '@/utils/i18n';
import React from 'react';
import { formatFulfillmentSummary } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-header';
import { GetAvailableOrderFulfillmentResponse } from '@/types';
import { Icons, List, Typography, XStack } from '@usepepper/sprout-kit-ui';
import { OrderEntryMobileHeaderPanelListItem } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-header-panel-list-item';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import { TimeZoneMap } from '@domain/fulfillment-settings/types/types';

interface OrderEntryMobileHeaderPanelDetailsProps {
  availableFulfillments: GetAvailableOrderFulfillmentResponse[];
  // Absent when the customer has no cutoff, not when one is still loading
  cutoffTimeString?: string;
  isFulfillmentDisabled?: boolean;
  onPressFulfillment?: () => void;
  onPressOrderGuide?: () => void;
  orderGuideName: string;
  // Absent when the customer has no minimum, not when one is still loading
  orderMinimumDisplayString?: string;
  savedSelectedFulfillment: SavedSelectedFulfillment;
  timezones: TimeZoneMap;
}

export const OrderEntryMobileHeaderPanelDetails: React.FC<
  OrderEntryMobileHeaderPanelDetailsProps
> = ({
  availableFulfillments,
  cutoffTimeString,
  isFulfillmentDisabled,
  onPressFulfillment,
  onPressOrderGuide,
  orderGuideName,
  orderMinimumDisplayString,
  savedSelectedFulfillment,
  timezones,
}) => {
  const constraints = [
    orderMinimumDisplayString && {
      icon: <Icons.ArrowDownToLine color="$iconTertiary" size={15} />,
      key: 'minimum',
      text: `${i18n.t('sales_portal.fulfillment_settings.minimum')} ${orderMinimumDisplayString}`,
    },
    cutoffTimeString && {
      icon: <Icons.Clock color="$iconTertiary" size={15} />,
      key: 'cutoff',
      text: `${i18n.t('sales_portal.fulfillment_settings.cutoff')} ${cutoffTimeString}`,
    },
  ].filter((constraint) => !!constraint);

  return (
    <>
      <List>
        <List.Separator borderColor="$borderPrimary" />

        <OrderEntryMobileHeaderPanelListItem
          disabled={isFulfillmentDisabled}
          leading={<Icons.Calendar color="$iconPrimary" size={24} />}
          onPress={onPressFulfillment}
          title={formatFulfillmentSummary(
            savedSelectedFulfillment,
            availableFulfillments,
            timezones,
          )}
        />

        <List.Separator borderColor="$borderPrimary" />

        <OrderEntryMobileHeaderPanelListItem
          leading={<Icons.List color="$iconPrimary" size={24} />}
          onPress={onPressOrderGuide}
          title={orderGuideName}
        />
      </List>

      {constraints.length > 0 ? (
        <XStack
          alignItems="center"
          gap="$16"
          justifyContent="center"
          marginTop="$8"
        >
          {constraints.map(({ icon, key, text }) => (
            <XStack alignItems="center" gap="$4" key={key}>
              {icon}
              <Typography color="$textTertiary" size="$sm">
                {text}
              </Typography>
            </XStack>
          ))}
        </XStack>
      ) : null}
    </>
  );
};
